import React from 'react'
import Tooltip from '../../ToolTip'
import { useModals } from '@/modules/drivers/stores/modalsStore'
import { faCaretUpSolid, FaIcon, faPrintSolid } from '@/lib/fa'
import ContractActions from '../details/ContractActions'
import PrintActions from '../details/PrintActions'
import AddContractColor from '../../../../../assets/icons_new/AddContractColor.svg'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import type { SelectionGroup } from '@/modules/drivers/context/TableContext'
import PrintGroupedContractTypeModal from '@/modules/drivers/modals/contract/PrintGroupedContractTypeModal'
import { toast } from '@/lib/toasts/api'

const ActionsContainer = ({
  selectedGroup,
  companyId,
}: {
  selectedGroup: SelectionGroup
  companyId: string
}) => {
  const {
    isPrintActionsOpen,
    setIsPrintActionsOpen,
    isAddActionsOpen,
    setIsAddActionsOpen,
  } = useModals()
  const { t } = useLocalize()
  const isDisabledIfGroupInactive = !selectedGroup?.isActive

  const [isGroupedContractPrintOpen, setIsGroupedContractPrintOpen] =
    React.useState(false)
  const [groupedContractPrintDriverIds, setGroupedContractPrintDriverIds] =
    React.useState<number[]>([])

  const openGroupedContractPrintModal = React.useCallback(() => {
    const rawIds = selectedGroup?.ids ?? []
    const driverIds = rawIds
      .map((id) => (typeof id === 'string' ? parseInt(id, 10) : id))
      .filter((id): id is number => typeof id === 'number' && !Number.isNaN(id))

    if (driverIds.length === 0) {
      toast.warning({ text: t('driverModule.select-drivers') })
      return
    }
    setGroupedContractPrintDriverIds(driverIds)
    setIsPrintActionsOpen(false)
    setIsGroupedContractPrintOpen(true)
  }, [selectedGroup, t, setIsPrintActionsOpen])
  
  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-5">
      <div className="tw-relative">
        <div className="tw-flex tw-w-fit tw-items-center">
          <Tooltip
            text={t('contract.add-contract')}
            disabled={isDisabledIfGroupInactive}
            onClick={() => {
              setIsAddActionsOpen((prev) => {
                const next = !prev
                if (next) setIsPrintActionsOpen(false)
                return next
              })
            }}
            className="tw-flex tw-items-center tw-gap-1"
          >
            <img src={AddContractColor} alt="Add Contract" />
            <FaIcon
              icon={faCaretUpSolid}
              className={`tw-transition-all ${isAddActionsOpen ? 'tw-rotate-180' : ''}`}
            />
          </Tooltip>
        </div>

        {isAddActionsOpen && <ContractActions />}
      </div>
      <div className="tw-hidden tw-h-5 tw-w-0.5 tw-bg-secondary lg:tw-block"></div>
      <div className="tw-relative">
        <div className="tw-flex tw-w-fit tw-items-center">
          <Tooltip
            text={t('common.print')}
            disabled={isDisabledIfGroupInactive}
            onClick={() => {
              setIsPrintActionsOpen((prev) => {
                const next = !prev
                if (next) setIsAddActionsOpen(false)
                return next
              })
            }}
            className="tw-flex tw-items-center tw-gap-1"
          >
            <FaIcon icon={faPrintSolid} className="tw-text-primary" />
            <FaIcon
              icon={faCaretUpSolid}
              className={`tw-transition-all ${isPrintActionsOpen ? 'tw-rotate-180' : ''}`}
            />
          </Tooltip>
        </div>
        {isPrintActionsOpen && (
          <PrintActions
            companyId={companyId}
            onOpenGroupedContractPrint={openGroupedContractPrintModal}
          />
        )}
      </div>
      <PrintGroupedContractTypeModal
        isOpen={isGroupedContractPrintOpen}
        onClose={() => setIsGroupedContractPrintOpen(false)}
        companyId={companyId}
        driverIds={groupedContractPrintDriverIds}
      />
    </div>
  )
}

export default ActionsContainer
